import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ClipboardList, Send, Loader2, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function RelanceFormulePanel() {
  const [data, setData] = useState({ count: 0, clients: [] });
  const [conversions, setConversions] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/admin/no-plan-nudge/list`, { withCredentials: true }),
        axios.get(`${API}/admin/no-plan-nudge/email-preview`, { withCredentials: true }),
        axios.get(`${API}/admin/stats/relance-conversions`, { withCredentials: true }).catch(() => null),
      ]);
      setData(r1.data);
      setPreview(r2.data);
      if (r3) setConversions(r3.data);
    } catch (e) {
      toast.error('Impossible de charger la liste des clientes sans formule.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const pending = data.clients.filter((c) => !c.manual_relance_sent_at);

  const send = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/no-plan-nudge/send-manual`, {}, { withCredentials: true });
      const { sent, failed = [] } = res.data;
      if (sent > 0) toast.success(`${sent} relance${sent > 1 ? 's' : ''} envoyée${sent > 1 ? 's' : ''} avec succès.`);
      else toast.info('Aucune relance à envoyer.');
      if (failed.length) toast.error(`${failed.length} envoi${failed.length > 1 ? 's' : ''} en échec.`);
      setConfirm(false);
      load();
    } catch (e) {
      toast.error("Erreur pendant l'envoi de la relance.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-[#E9DDCC] rounded-2xl">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-[#C99A55]/30 rounded-2xl bg-gradient-to-br from-[#F3EADA] to-[#F5EFE5]" data-testid="relance-formule-panel">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ClipboardList className="w-4 h-4 text-[#C99A55]" strokeWidth={1.8} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#C99A55] font-semibold">Relance formule</span>
            </div>
            <h3 className="font-heading text-lg text-[#493329]">Clientes inscrites sans formule choisie</h3>
          </div>
          <Button onClick={load} variant="outline" size="sm" className="rounded-full text-xs h-8 border-[#E9DDCC] text-[#7A6455]" data-testid="relance-refresh-btn">
            Actualiser
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl p-3 text-center border border-[#E9DDCC]">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Sans formule</div>
            <div className="font-heading text-2xl text-[#493329]" data-testid="relance-total">{data.count}</div>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-[#C99A55]/40">
            <div className="text-[10px] uppercase text-[#C99A55] tracking-wider mb-1">À relancer</div>
            <div className="font-heading text-2xl text-[#C99A55]" data-testid="relance-pending">{pending.length}</div>
          </div>
        </div>

        {data.clients.length === 0 ? (
          <div className="flex items-center gap-2 bg-white rounded-xl p-4 border border-[#E9DDCC] text-sm text-[#7A6455]" data-testid="relance-empty">
            <CheckCircle2 className="w-4 h-4 text-[#7C9C82]" />
            Toutes les clientes ont choisi leur formule. Parfait !
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#E9DDCC] max-h-[300px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F5EFE5] text-[10px] uppercase tracking-wider text-[#AE9885]">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Cliente</th>
                  <th className="text-left px-2 py-2 font-medium">Email confirmé</th>
                  <th className="text-left px-2 py-2 font-medium">Relance auto</th>
                  <th className="text-left px-2 py-2 font-medium">Relance manuelle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EFE4D0]">
                {data.clients.map((c, i) => (
                  <tr key={i} data-testid={`relance-row-${i}`}>
                    <td className="px-4 py-2.5">
                      <span className="text-[#493329] font-medium block truncate">{c.name || '—'}</span>
                      <span className="text-[#7A6455] text-xs truncate">{c.email}</span>
                    </td>
                    <td className="px-2 py-2.5 text-xs">{c.confirmed ? 'Oui' : 'Non'}</td>
                    <td className="px-2 py-2.5 text-xs text-[#7A6455]">{c.auto_nudge_sent_at ? new Date(c.auto_nudge_sent_at).toLocaleDateString('fr-FR') : '—'}</td>
                    <td className="px-2 py-2.5 text-xs text-[#7A6455]">{c.manual_relance_sent_at ? new Date(c.manual_relance_sent_at).toLocaleDateString('fr-FR') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {conversions && conversions.total_relancees > 0 && (
          <div className="rounded-xl border border-[#E9DDCC] bg-white p-4" data-testid="relance-efficacite-block">
            <div className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-2">Efficacité des relances</div>
            <div className="grid grid-cols-3 gap-3 text-center mb-3">
              <div className="bg-[#F5EFE5] rounded-lg p-2.5 border border-[#E9DDCC]">
                <div className="text-[10px] uppercase text-[#AE9885]">Relancées</div>
                <div className="font-heading text-xl text-[#493329]" data-testid="relance-conv-total">{conversions.total_relancees}</div>
              </div>
              <div className="bg-[#F5EFE5] rounded-lg p-2.5 border border-[#7C9C82]/40">
                <div className="text-[10px] uppercase text-[#7C9C82]">Ont choisi une formule</div>
                <div className="font-heading text-xl text-[#7C9C82]" data-testid="relance-conv-converted">{conversions.converties}</div>
              </div>
              <div className="bg-[#F5EFE5] rounded-lg p-2.5 border border-[#C99A55]/40">
                <div className="text-[10px] uppercase text-[#C99A55]">Taux de conversion</div>
                <div className="font-heading text-xl text-[#C99A55]" data-testid="relance-conv-rate">{conversions.taux_conversion} %</div>
              </div>
            </div>
            {conversions.converted.length > 0 && (
              <ul className="divide-y divide-[#EFE4D0] text-sm max-h-[180px] overflow-y-auto" data-testid="relance-conv-list">
                {conversions.converted.map((c, i) => (
                  <li key={i} className="flex items-center justify-between py-2">
                    <span className="text-[#493329] font-medium truncate">{c.name || c.email}</span>
                    <span className="text-xs text-[#7A6455] ml-2">→ {c.chosen_plan} · relance {c.relance_type}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <Button
          onClick={() => setShowPreview(!showPreview)}
          variant="outline"
          className="w-full rounded-xl h-10 border-[#E9DDCC] text-[#7A6455] text-sm"
          data-testid="relance-preview-toggle"
        >
          {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
          {showPreview ? "Masquer l'aperçu de l'email" : "Voir l'email de relance avant envoi"}
        </Button>

        {showPreview && preview && (
          <div className="bg-white rounded-xl border border-[#E9DDCC] overflow-hidden" data-testid="relance-email-preview">
            <div className="px-4 py-2.5 border-b border-[#EFE4D0] text-xs text-[#7A6455]">
              <strong className="text-[#493329]">Sujet :</strong> {preview.subject}
            </div>
            <iframe title="Aperçu email relance" srcDoc={preview.html} className="w-full h-[420px] border-0" sandbox="" />
          </div>
        )}

        {pending.length > 0 && (
          !confirm ? (
            <Button
              onClick={() => setConfirm(true)}
              className="w-full bg-[#C99A55] hover:bg-[#493329] text-white rounded-xl h-12 transition-all duration-300"
              data-testid="relance-send-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Envoyer la relance aux {pending.length} cliente{pending.length > 1 ? 's' : ''}
            </Button>
          ) : (
            <div className="bg-[#FFF8E6] border border-[#C99A55]/40 rounded-xl p-4">
              <div className="flex items-start gap-2 mb-3">
                <AlertCircle className="w-4 h-4 text-[#C99A55] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#493329]">
                  Confirmez-vous l'envoi de l'email « {preview?.subject} » à <strong>{pending.length}</strong> destinataire{pending.length > 1 ? 's' : ''} ? Chaque cliente ne recevra cette relance qu'une seule fois.
                </p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setConfirm(false)} variant="outline" className="flex-1 rounded-xl h-10 border-[#E9DDCC] text-[#7A6455]" disabled={sending} data-testid="relance-cancel-send">
                  Annuler
                </Button>
                <Button onClick={send} className="flex-1 bg-[#C99A55] hover:bg-[#493329] text-white rounded-xl h-10" disabled={sending} data-testid="relance-confirm-send">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Oui, envoyer'}
                </Button>
              </div>
            </div>
          )
        )}

        <p className="text-[11px] text-[#AE9885] italic text-center">
          Aucun email n'est envoyé sans votre confirmation. Envoi unique par cliente (anti-doublon automatique).
        </p>
      </CardContent>
    </Card>
  );
}
